import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PagesRoutes } from "@app/router";
import { useForgotPasswordApi } from "@services/auth";
import { Button } from "@shared/components";
import { useTranslation } from "@shared/locales";
import { validateEmail } from "../utils";
import { AuthShell } from "./AuthShell";
import { FormField } from "./FormField";

/**
 * Step 1 of the reset flow — requests the emailed OTP, then hands off to
 * `/auth/reset-password` with the address prefilled.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>();
  const [serverError, setServerError] = useState<string | null>(null);

  const forgotPassword = useForgotPasswordApi({
    withoutToastMessage: true,
    navigateOnError: false,
    onSuccess: () =>
      navigate(
        `${PagesRoutes.auth["reset-password"]}?email=${encodeURIComponent(email)}`
      ),
    onError: (error) =>
      setServerError(error?.message ?? t("auth.errors.accountNotFound")),
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setServerError(null);

    const nextError = validateEmail(email);
    setEmailError(nextError);
    if (nextError) return;

    forgotPassword.mutate({ email });
  };

  return (
    <AuthShell
      title={t("auth.forgot.title")}
      description={t("auth.forgot.description")}
      serverError={serverError}
      footer={
        <Link
          to={PagesRoutes.auth.login}
          className="font-medium text-foreground hover:underline"
        >
          {t("auth.forgot.backToLogin")}
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <FormField
          id="forgot-email"
          type="email"
          label={t("auth.fields.email")}
          placeholder={t("auth.fields.emailPlaceholder")}
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={emailError && t(emailError)}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={forgotPassword.isPending}
        >
          {forgotPassword.isPending
            ? t("auth.forgot.submitting")
            : t("auth.forgot.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
